/**
 * Maps API snake_case assignment responses to camelCase domain interfaces.
 */
import { AssignmentStatus } from "@/ts/enums/AssignmentStatusEnum";
import {
  SubmissionStatus,
  mapRawSubmissionStatus
} from "@shared/assignments/utils/submissionStatus";
import type {
  AssignmentApiResponse,
  AssignmentCurriculumConnectionApiResponse,
  AssignmentDetailApiResponse,
  AssignmentSubmissionApiResponse,
  AssignmentAssetApiResponse
} from "@ts/interfaces/assignments/api/assignmentsApiInterface";
import type {
  Assignment,
  AssignmentBlocks,
  AssignmentCurriculumConnection,
  AssignmentDetail,
  AssignmentDeliverable,
  AssignmentDocAsset,
  AssignmentExemption,
  AssignmentFileAsset,
  AssignmentResource,
  AssignmentSubmission,
  RubricBlocks,
  SupportGuideBlocks
} from "@ts/interfaces/assignments/assignmentsInterface";

/* ------------------------------------------------------------------ */
/*  Status mapping                                                    */
/* ------------------------------------------------------------------ */

const SUBMISSION_STATUS_TO_ASSIGNMENT_STATUS: Record<
  SubmissionStatus,
  AssignmentStatus
> = {
  [SubmissionStatus.TODO]: AssignmentStatus.TODO,
  [SubmissionStatus.PENDING_MARKS]: AssignmentStatus.PENDING,
  [SubmissionStatus.MARKED]: AssignmentStatus.MARKED,
  [SubmissionStatus.EXEMPTED]: AssignmentStatus.EXEMPTED,
  [SubmissionStatus.RESUBMISSION_REQUIRED]: AssignmentStatus.RESUBMISSION_REQUIRED
};

const mapSubmissionStatus = (raw: string | null | undefined): AssignmentStatus =>
  SUBMISSION_STATUS_TO_ASSIGNMENT_STATUS[mapRawSubmissionStatus(raw)];

export const mapAssignmentStatus = (
  latestSubmission: AssignmentSubmissionApiResponse | null
): AssignmentStatus => {
  if (!latestSubmission) return AssignmentStatus.TODO;

  return mapSubmissionStatus(latestSubmission.status);
};

/* ------------------------------------------------------------------ */
/*  Single-field mappers                                              */
/* ------------------------------------------------------------------ */

const mapCurriculumConnection = (
  items: AssignmentCurriculumConnectionApiResponse[] | null | undefined
): AssignmentCurriculumConnection[] =>
  (items ?? []).map(c => ({
    curriculumCode: c.curriculum_code,
    curriculumDescription: c.curriculum_description
  }));

const mapFileAsset = (asset: AssignmentAssetApiResponse): AssignmentFileAsset => ({
  uuid: asset.uuid,
  assetId: asset.asset_id,
  name: asset.name,
  downloadLink: asset.download_link,
  type: asset.type,
  childAssets: asset.child_assets?.map(mapFileAsset) ?? null
});

const mapSubmission = (
  sub: AssignmentSubmissionApiResponse
): AssignmentSubmission => ({
  uuid: sub.uuid,
  submittedOn: sub.submitted_on,
  marks: sub.marks,
  marksPercentage: sub.marks_percentage ?? null,
  markerConclusion: sub.marker_conclusion ?? null,
  isMarkOfRecord: sub.is_mark_of_record ?? false,
  type: sub.type,
  status: mapSubmissionStatus(sub.status),
  maxMark: sub.max_mark,
  overallComment: sub.overall_comment,
  assignmentName: sub.assignment_name,
  subject: sub.subject,
  viewedAt: sub.viewed_at,
  rejectReasons: sub.reject_reasons ?? [],
  markerAssessmentAssets: (sub.marker_assessment_assets ?? []).map(mapFileAsset),
  studentSubmissionAssets: (sub.student_submission_assets ?? []).map(mapFileAsset)
});

/* ------------------------------------------------------------------ */
/*  List mapping                                                      */
/* ------------------------------------------------------------------ */

export const mapAssignmentList = (
  items: AssignmentApiResponse[]
): Assignment[] =>
  items.map(item => ({
    uuid: item.uuid,
    name: item.name,
    subject: item.subject ?? "",
    term: item.term,
    maxMark: item.max_mark,
    highestMarks: item.highest_marks ?? 0,
    markedCount: item.marked_count ?? 0,
    status: mapAssignmentStatus(item.latest_submission),
    description: item.description ?? "",
    purpose: item.purpose ?? null,
    learningOutcome: item.learning_outcome ?? [],
    curriculumConnection: mapCurriculumConnection(item.curriculum_connection),
    latestSubmission: item.latest_submission
      ? mapSubmission(item.latest_submission)
      : null,
    plpGradeCode: item.plp_grade_code ?? 0,
    plpGradeName: item.plp_grade_name ?? ""
  }));

/* ------------------------------------------------------------------ */
/*  Detail mapping                                                    */
/* ------------------------------------------------------------------ */

export const mapAssignmentDetail = (
  data: AssignmentDetailApiResponse
): AssignmentDetail => {
  const assignment: Assignment = {
    uuid: data.assignment.uuid,
    name: data.assignment.name,
    subject: data.assignment.subject ?? "",
    term: data.assignment.term,
    maxMark: data.assignment.max_mark,
    highestMarks: data.assignment.highest_marks ?? 0,
    markedCount: data.assignment.marked_count ?? 0,
    status: mapAssignmentStatus(data.assignment.latest_submission),
    description: data.assignment.description ?? "",
    purpose: data.assignment.purpose ?? null,
    learningOutcome: data.assignment.learning_outcome ?? [],
    curriculumConnection: mapCurriculumConnection(
      data.assignment.curriculum_connection
    ),
    latestSubmission: data.assignment.latest_submission
      ? mapSubmission(data.assignment.latest_submission)
      : null,
    // PLP fields: non-zero when this assignment belongs to a PLP subject sourced
    // from a different grade (e.g. a G10 student viewing a G8 English assignment).
    // Used in AssignmentInfoBar to show the "(PLP - Grade X)" label on the subject strap.
    plpGradeCode: data.assignment.plp_grade_code ?? 0,
    plpGradeName: data.assignment.plp_grade_name ?? ""
  };

  const assets: AssignmentDocAsset[] = (data.assets ?? []).map(a => ({
    id: a.id,
    downloadLink: a.download_link,
    fileName: a.file_name,
    fileType: a.file_type,
    size: a.size,
    type: a.type
  }));

  const resources: AssignmentResource[] = (data.resources ?? []).map(r => ({
    uuid: r.uuid,
    downloadLink: r.download_link,
    downloadable: r.downloadable,
    fileName: r.file_name,
    fileType: r.file_type,
    size: r.size,
    type: r.type,
    websiteLink: r.website_link ?? null
  }));

  const deliverables: AssignmentDeliverable[] = (data.deliverables ?? []).map(
    d => ({
      uuid: d.uuid,
      allowLink: d.allow_link === 1,
      allowUpload: d.allow_upload === 1,
      description: d.description,
      mandatory: d.mandatory === 1,
      fileTypes: d.file_type ?? []
    })
  );

  const previousSubmissions: AssignmentSubmission[] = (
    data.previous_submissions ?? []
  ).map(mapSubmission);

  const exemptions: AssignmentExemption[] = (data.exemptions ?? []).map(e => ({
    uuid: e.uuid,
    status: e.status,
    reason: e.reason
  }));

  // The blocks are nested inside data.assignment (not at the top level of the
  // detail response) — the API embeds them in the assignment object itself.
  const assignmentBlocks: AssignmentBlocks | undefined =
    data.assignment.assignment_blocks
      ? {
          assignmentOverview: data.assignment.assignment_blocks.assignment_overview,
          instructions: data.assignment.assignment_blocks.instructions,
          whatToInclude: data.assignment.assignment_blocks.what_to_include,
          whatSuccessLooksLike:
            data.assignment.assignment_blocks.what_success_looks_like
        }
      : undefined;

  const rubricBlocks: RubricBlocks | undefined =
    data.assignment.rubric_blocks?.marking_rubric
      ? { markingRubric: data.assignment.rubric_blocks.marking_rubric }
      : undefined;

  const supportGuideApiBlocks = data.assignment.support_guide_blocks;
  const supportGuideBlocks: SupportGuideBlocks | undefined =
    supportGuideApiBlocks &&
    (
      supportGuideApiBlocks.support_guide ||
      supportGuideApiBlocks.getting_started ||
      supportGuideApiBlocks.helpful_tip ||
      supportGuideApiBlocks.support_guide_steps
    )
      ? {
          supportGuide: supportGuideApiBlocks.support_guide,
          gettingStarted: supportGuideApiBlocks.getting_started,
          helpfulTip: supportGuideApiBlocks.helpful_tip,
          supportGuideSteps: supportGuideApiBlocks.support_guide_steps
        }
      : undefined;

  // Extract the combined PDF URL from the assets list (Slice 10a/10b).
  // Null when absent — all three Download PDF CTAs remain non-functional.
  const combinedPdfUrl =
    assets.find(a => a.type === "combined")?.downloadLink ?? null;

  return {
    assignment,
    assets,
    resources,
    deliverables,
    previousSubmissions,
    exemptions,
    hasExceededAllowedExemptions: data.has_exceeded_allowed_exemptions ?? false,
    canExempt: data.can_exempt ?? false,
    isExempted: data.is_exempted ?? false,
    assignmentBlocks,
    rubricBlocks,
    supportGuideBlocks,
    assignmentImageUrl: data.assignment.assignment_image_url ?? null,
    assignmentImageAlt: data.assignment.assignment_image_alt ?? null,
    combinedPdfUrl
  };
};
